import { CustomAvatarProps, TmdxAvatar } from '@ds';
import { useMemo } from 'react';
import { EmployeeLabels, OrganType } from '@/api/apis/case';
import { ChipWithStatus, ChipWithStatusProps } from '../ChipWithStatus/ChipWithStatus';
import { getAvatarChipConfig, ResourceAvatarStatusChip } from './utils';

type ExtraResourceProps = {
  organ?: OrganType | null;
  isStatus?: boolean;
  chipProps?: ChipWithStatusProps;
  isPrimary?: boolean;
  label?: EmployeeLabels;
  statusChip?: ResourceAvatarStatusChip;
};

export type ResourceAvatarProps = CustomAvatarProps & ExtraResourceProps;

export const ResourceAvatar = ({
  organ,
  type,
  isStatus,
  chipProps,
  isPrimary,
  label,
  statusChip,
  children,
  ...props
}: ResourceAvatarProps) => {
  const bgColors = useMemo(() => {
    if (type === 'scs') {
      return {
        base: 'bg-black text-base-90 rounded-full',
      };
    }

    if (type !== 'device') {
      return {};
    }

    switch (organ) {
      case OrganType.Heart:
        return {
          base: 'bg-red-50 text-base-10',
        };
      case OrganType.Liver:
        return {
          base: 'bg-green-50 text-base-10',
        };
      case OrganType.Lungs:
        return {
          base: 'bg-blue-50 text-base-10',
        };
      case OrganType.Kidney:
        return {
          base: 'bg-yellow-50 text-base-10',
        };
      default:
        return {};
    }
  }, [organ, type]);

  const avatarChipConfig = getAvatarChipConfig({
    chipProps,
    isPrimary,
    label,
    statusChip,
    children,
  });

  if (isStatus && avatarChipConfig) {
    return (
      <div className={`inline-flex flex-col items-center ${avatarChipConfig.hasBottomMargin ? 'mb-[3px]' : ''}`}>
        <TmdxAvatar {...props} size="lg" className="-mb-0.75 min-w-12 min-h-12" classNames={bgColors} type={type} />

        <ChipWithStatus
          variant={avatarChipConfig.variant}
          size="xxs"
          color={avatarChipConfig.color}
          className={avatarChipConfig.className}
          {...chipProps}
        >
          {avatarChipConfig.content}
        </ChipWithStatus>
      </div>
    );
  }

  return <TmdxAvatar {...props} classNames={bgColors} type={type} />;
};
